/**
 * One control for "how long" and "finished by when".
 *
 * They are the same span seen from two ends — it starts now — so they live in one block
 * rather than two modes. Whichever the user last touched becomes the fixed end, and the
 * other is derived and updates as the clock moves. Which one is fixed is stated in
 * words, because that is the promise the app is making.
 *
 * The number is a plain text input with a numeric keypad, not `type="number"`: stepper
 * arrows invite thirty taps to get from 20 to 50, and this screen is used by someone
 * half awake. Presets stay as one-tap shortcuts.
 *
 * A chosen length that no recording can reach is shown as it was chosen, with the
 * nearest lengths that do work offered as buttons. It is not corrected, snapped, or
 * marked as an error — the corpus not stretching that far is a fact about the corpus,
 * not a mistake the user made.
 *
 * Everything that used to be on its own line is now on a shared one: the finish time
 * rides alongside the number, and which end is fixed, when it finishes and how many
 * recordings can do it are a single sentence. The home screen has one viewport and no
 * scrollbar, so a line is a real cost.
 */

import { useMemo } from 'preact/hooks';
import type { Cadence } from '../types';
import { reachable, reachableDurations } from '../state/manifest';
import {
  type TimingIntent, finishMs, formatClock, formatExact, intent, isExpired,
  isTomorrow, nextOccurrence, nowMs, parseClock, targetSec, toClockValue,
} from '../state/timing';

const CADENCE_NAME = { opening: 'Minimal', occasional: 'Occasional', frequent: 'Frequent' } as const;

/**
 * The lengths offered as one-tap chips.
 *
 * Six of them, because six is what a 320 px screen fits on one row at a usable width,
 * and a row that wraps to three is how this screen came to need scrolling. Which six is
 * measured rather than chosen. Against the current 248-recording archive: 10 minutes is
 * reachable by 2 recordings and 15 by 38, so 10 was the one chip that was dead on
 * arrival. 35 (103) and 40 (114) sit between 30 (100) and 45 (137) and buy nothing the
 * number field does not. 75 and 90 reach 243 each but are lengths people type rather
 * than tap for. Every one of them is still reachable by typing; see MAX_MINUTES.
 */
const PRESETS = [15, 20, 25, 30, 45, 60].map((m) => m * 60);

/**
 * The longest length that may be typed.
 *
 * Not a technical limit. Each sitting period may be stretched to an hour, so a
 * recording with ten gaps reports itself as reaching ten hours — six of them do — and a
 * mistyped "600" would be accepted as a ten-hour sit that is almost entirely
 * synthesised room tone. Two hours is past the longest preset and past any sit these
 * recordings were made for.
 */
const MAX_MINUTES = 120;

/** An exact promise gets no tolerance; see canReach in session/plan.ts. */
const EXACT = 0;

/** The nearest lengths either side that some recording can actually do. */
function nearestReachable(sec: number, cadence: Cadence, tol?: number): number[] {
  const out: number[] = [];
  for (let step = 60; step <= 20 * 60; step += 60) {
    for (const cand of [sec - step, sec + step]) {
      if (cand < 60) continue;
      if (reachable(cand, cadence, tol).length > 0) out.push(cand);
    }
    if (out.length >= 2) break;
  }
  return out.slice(0, 2).sort((a, b) => a - b);
}

interface Props { cadence: Cadence }

export function TimeSpan({ cadence }: Props) {
  const it = intent.value;
  const now = nowMs.value;

  const expired = isExpired(it, now);
  const target = targetSec(it, now);
  const finish = finishMs(it, now);
  const fixedFinish = it.kind === 'deadline';

  // A deadline is an exact promise about a clock, so it is checked without the
  // ±30 s slack that a round preset can afford.
  const tol = fixedFinish ? EXACT : undefined;

  const pool = useMemo(
    () => (expired || target < 60 ? [] : reachable(target, cadence, tol)),
    [target, cadence, expired, tol],
  );
  // Since cadence became a speech band, "no recording is this long" can be false: there are
  // recordings of this length, just none with this much (or this little) of his voice.
  const otherCadenceFits = useMemo(
    () => pool.length === 0 && !expired && target >= 60
      && (['opening', 'occasional', 'frequent'] as const)
        .some((c) => c !== cadence && reachable(target, c, tol).length > 0),
    [pool, target, cadence, expired, tol],
  );
  const possible = useMemo(
    () => reachableDurations(cadence, PRESETS),
    [cadence],
  );
  const suggestions = useMemo(
    () => (pool.length || expired || target < 60 ? [] : nearestReachable(target, cadence, tol)),
    [pool.length, target, cadence, expired, tol],
  );

  const setDuration = (sec: number) => { intent.value = { kind: 'duration', durationSec: sec }; };

  const onMinutes = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return;                       // keep the last good value; see onBlur
    const mins = Math.min(MAX_MINUTES, Math.max(1, Number(digits)));
    setDuration(mins * 60);
  };

  const onClock = (value: string) => {
    const hm = parseClock(value);
    if (!hm) return;
    intent.value = { kind: 'deadline', deadlineMs: nextOccurrence(hm.hh, hm.mm).getTime() };
  };

  const minutesShown = Math.max(0, Math.round(target / 60));

  return (
    <div class="span">
      {/* The length, and the far end of the same span, on one line. */}
      <div class="span-head">
        <input
          class="span-number"
          type="text"
          inputMode="numeric"
          autocomplete="off"
          aria-label="Session length in minutes"
          value={String(minutesShown)}
          onFocus={(e) => (e.currentTarget as HTMLInputElement).select()}
          onInput={(e) => onMinutes((e.currentTarget as HTMLInputElement).value)}
          onBlur={(e) => { (e.currentTarget as HTMLInputElement).value = String(minutesShown); }}
          style={{ width: `${Math.max(2, String(minutesShown).length)}ch` }}
        />
        <span class="span-unit">min</span>

        <div class="span-finish">
          <label class="eyebrow" for="finish-at">Ends</label>
          <input
            id="finish-at"
            type="time"
            step={60}
            value={toClockValue(finish)}
            onChange={(e) => onClock((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
      </div>

      {/* Which end is fixed, and whether it can be done — one sentence, because it used
          to be three lines and they were read as three unrelated facts.

          The finish time itself is deliberately NOT repeated here when the length is the
          fixed end: the field above already shows it, and it shows it in the browser's
          own clock format, so the two readings disagreed on a 24-hour device — "ends
          2:57 PM" under a box saying 14:57. */}
      <p class="span-note" aria-live="polite">
        {expired
          ? 'That time has gone by. Choose a finish time still to come.'
          : <>
              {fixedFinish
                ? <>Finish fixed · runs {formatExact(target, true)}</>
                : <>Length fixed{isTomorrow(finish, now) && ' · ends tomorrow'}</>}
              {' · '}
              {pool.length > 0
                ? `${pool.length} recording${pool.length === 1 ? '' : 's'} fit`
                : otherCadenceFits
                  ? `no recording gives ${CADENCE_NAME[cadence]} guidance at this length`
                  : 'no recording is this long'}
              {fixedFinish && <>
                {' · '}
                <button class="link" onClick={() => setDuration(Math.max(60, target))}>
                  fix the length instead
                </button>
              </>}
            </>}
      </p>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend class="visually-hidden">Common lengths</legend>
        <div class="chip-row" style={{ '--cols': PRESETS.length }}>
          {PRESETS.map((sec) => {
            const ok = possible.has(sec);
            return (
              <button
                key={sec}
                class="chip"
                aria-pressed={!fixedFinish && target === sec}
                aria-disabled={!ok}
                title={ok ? undefined : 'No recording can be made this length'}
                style={ok ? undefined : { opacity: 0.35, textDecoration: 'line-through' }}
                onClick={() => ok && setDuration(sec)}
              >
                <span class="fig">{sec / 60}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {suggestions.length > 0 ? (
        <div class="row" style={{ gap: 'var(--gap-1)', flexWrap: 'wrap' }}>
          <span class="cadence-hint">Nearest that works:</span>
          {suggestions.map((sec) => (
            <button key={sec} class="chip" style={{ minHeight: '2.4rem' }}
                    onClick={() => setDuration(sec)}>
              {fixedFinish
                ? `Finish at ${formatClock(now + sec * 1000)}`
                : `${Math.round(sec / 60)} min`}
            </button>
          ))}
        </div>
      ) : (
        /* The six chips are shortcuts, not the range. Said once, quietly, because the
           underlined number is an affordance people do not always read as one. */
        <p class="cadence-hint">Tap the number for any length up to {MAX_MINUTES / 60} hours.</p>
      )}
    </div>
  );
}

export function canBegin(it: TimingIntent, cadence: Cadence, now = Date.now()): boolean {
  if (isExpired(it, now)) return false;
  const sec = targetSec(it, now);
  if (sec < 60) return false;
  return reachable(sec, cadence, it.kind === 'deadline' ? EXACT : undefined).length > 0;
}
